import { Contribution } from './comparisonUtils';

// `percent` is divided by |a| so its sign is the direction of movement even from a negative baseline.
export type ImpactChange = { kind: 'value'; percent: number } | { kind: 'none' } | { kind: 'from-zero' };

export const computeChange = (a?: number, b?: number): ImpactChange => {
  if (a === undefined || b === undefined) return { kind: 'none' };
  if (a === b) return { kind: 'value', percent: 0 };
  if (a === 0) return { kind: 'from-zero' };
  return { kind: 'value', percent: ((b - a) / Math.abs(a)) * 100 };
};

export const formatChange = (change: ImpactChange): string => {
  switch (change.kind) {
    case 'value':
      if (change.percent === 0) return '0%';
      return `${change.percent > 0 ? '+' : '−'}${Math.round(Math.abs(change.percent))}%`;
    case 'from-zero':
      return 'from 0';
    default:
      return '—';
  }
};

export const CONTRIBUTOR_COLORS_LIGHT = ['green.800', 'green.600', 'green.500', 'green.300', 'green.100'];
// Dark backgrounds swallow green.700 and darker.
export const CONTRIBUTOR_COLORS_DARK = ['green.200', 'green.300', 'green.400', 'green.500', 'green.600'];

export const NO_BREAKDOWN_BG = { light: 'teal.400', dark: 'teal.500' };
export const NO_BREAKDOWN_HATCH = { light: 'rgba(255, 255, 255, 0.45)', dark: 'rgba(255, 255, 255, 0.25)' };

export const hatchBackgroundImage = (stripeColor: string) =>
  `repeating-linear-gradient(135deg, ${stripeColor} 0 4px, transparent 4px 8px)`;

export type ContributionSegment = Contribution & { color: string };

// Sort by magnitude, not signed value: negative shares (avoided burden) are major contributors.
const bySize = (x: Contribution, y: Contribution) => Math.abs(y.share) - Math.abs(x.share);

export const buildSegments = (contributions: Contribution[], colors: string[]): ContributionSegment[] => {
  const sorted = [...contributions].sort(bySize);
  const named = sorted.length > colors.length ? sorted.slice(0, colors.length - 1) : sorted;
  const rest = sorted.slice(named.length);

  const segments: Contribution[] = rest.length
    ? [
        ...named,
        {
          key: 'other',
          label: 'other',
          share: rest.reduce((sum, contribution) => sum + contribution.share, 0),
          impact: rest.reduce((sum, contribution) => sum + contribution.impact, 0),
        },
      ]
    : named;

  // Rank "other" by size like any segment: pinned last, a large remainder would read as the smallest contributor.
  return segments.sort(bySize).map((segment, index) => ({ ...segment, color: colors[index] }));
};
